import { WorkflowType } from '@communities/content-management/domain';
import { LabelSelector, type ScoreLabelOption, type Label } from '@core/components/LabelSelector';
import { LabelType } from '@core/graphql/types';
import { useCustomLabels } from '@system-config/label-management/hooks';
import { useMemo, useCallback } from 'react';

import { PRESENCE_CONFIG } from '../../constants';
import type { DialogCustomLabels, PresenceLabel } from '../../types';
import { useDialogContext } from '../DialogProvider';

import { stepFormStyles, inputGroupStyles } from './Steps.css';

/**
 * Map workflow type to custom label type filter.
 * Only moderation and classification workflows use custom labels.
 */
const WORKFLOW_TO_LABEL_TYPE: Partial<Record<WorkflowType, LabelType>> = {
  [WorkflowType.MODERATION]: LabelType.Moderation,
  [WorkflowType.CLASSIFICATION]: LabelType.Classification,
};

/**
 * CustomLabelsStep component props
 */
export interface CustomLabelsStepProps {
  communityId: string;
}

/**
 * CustomLabelsStep component - Moderation-only step
 * Uses the same LabelSelector component as text/image steps for visual consistency.
 * Custom labels are presence-based — no severity or score threshold.
 * Filters custom labels by labelType matching the current workflow.
 */
export function CustomLabelsStep({ communityId }: CustomLabelsStepProps) {
  const { state, actions, workflowType } = useDialogContext();
  const { customLabels } = state.formData;

  const { labels: availableCustomLabels, loading } = useCustomLabels(communityId);

  // Filter custom labels by label type matching the workflow
  const targetLabelType = WORKFLOW_TO_LABEL_TYPE[workflowType];

  // Workflow-aware empty state message
  const emptyStateMessage =
    workflowType === WorkflowType.CLASSIFICATION
      ? 'No custom labels available for this community. Create custom labels in the Classification Labels section first.'
      : 'No custom labels available for this community. Create custom labels in the Moderation Labels section first.';

  // Map active custom labels matching the workflow type to LabelSelector option format
  const customLabelOptions: ScoreLabelOption[] = useMemo(
    () =>
      availableCustomLabels
        .filter(label => label.active && label.labelType === targetLabelType)
        .map(label => ({
          id: label.id,
          labelName: label.name,
        })),
    [availableCustomLabels, targetLabelType]
  );

  // Convert PresenceLabel[] to Label[] for the LabelSelector
  const selectedAsScoreLabels: Label[] = useMemo(
    () => customLabels.customLabels.map(label => ({ labelId: label.labelId, scoreThreshold: 0 })),
    [customLabels.customLabels]
  );

  // Convert Label[] back to PresenceLabel[] with names
  const handleCustomLabelsChange = useCallback(
    (labels: Label[]) => {
      const updatedLabels: PresenceLabel[] = labels.map(label => {
        const labelInfo = customLabelOptions.find(opt => opt.id === label.labelId);

        return { labelId: label.labelId, labelName: labelInfo?.labelName };
      });

      const updatedCustomLabels: DialogCustomLabels = {
        customLabels: updatedLabels,
      };

      actions.updateFormData({ customLabels: updatedCustomLabels });
    },
    [actions, customLabelOptions]
  );

  return (
    <div className={stepFormStyles}>
      <div className={inputGroupStyles}>
        {customLabelOptions.length > 0 ? (
          <LabelSelector
            enableBulkSelection
            availableLabels={customLabelOptions}
            config={PRESENCE_CONFIG}
            emptyStateMessage="No custom labels selected. Add labels to include community-specific content criteria."
            loading={loading}
            selectedLabels={selectedAsScoreLabels}
            title="Custom Labels"
            onChange={handleCustomLabelsChange}
          />
        ) : (
          <div aria-live="polite" role="status">
            {loading ? <p>Loading custom labels...</p> : <p>{emptyStateMessage}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
